import { useEffect, type MouseEvent, type ReactNode } from 'react'
import { BottomNav } from './BottomNav'
import { ArtistNameplate } from '../components/ArtistNameplate'
import { CautionRail } from '../components/CautionRail'
import { GlyphIcon } from '../components/GlyphIcon'
import { TourSoundtrack } from '../components/TourSoundtrack'
import { withBasePath } from '../routing/basePath'
import { routeLabel, type RouteId } from '../routing/routeTable'

interface AppShellProps {
  activeRoute: RouteId
  authenticated: boolean
  children: ReactNode
  communityPath: string
  notificationCount: number
  online: boolean
  selectedCity: string
  onNavigate: (route: string) => void
}

const desktopItems: Array<{ href: string; ids: RouteId[]; label: string }> = [
  { href: '/home', ids: ['home'], label: 'Home' },
  { href: '/tour', ids: ['tour', 'show-detail', 'itinerary', 'check-in'], label: 'Tour' },
  { href: '/tickets', ids: ['tickets', 'ticket-connect', 'ticket-detail'], label: 'Tickets' },
  { href: '', ids: ['feed'], label: 'Community' },
  { href: '/passport', ids: ['passport'], label: 'Passport' },
  { href: '/content', ids: ['content', 'content-detail'], label: 'Tour Desk' },
]

const settingsRouteIds: RouteId[] = [
  'settings',
  'settings-accessibility',
  'settings-notifications',
  'settings-privacy',
]

const publicItems: ReadonlyArray<{ href: string; ids: RouteId[]; label: string }> = [
  { href: '/tour', ids: ['tour', 'show-detail'], label: 'Tour' },
  { href: '/content', ids: ['content', 'content-detail'], label: 'Tour Desk' },
  { href: '/content/content-music-links', ids: [], label: 'Official links' },
  { href: '/auth/sign-in', ids: ['login'], label: 'Sign in' },
]

export function AppShell({
  activeRoute,
  authenticated,
  children,
  communityPath,
  notificationCount,
  online,
  selectedCity,
  onNavigate,
}: AppShellProps) {
  useEffect(() => {
    document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true })
    window.scrollTo({ top: 0 })
  }, [activeRoute])

  const handleLink = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <div className="app-frame">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className={authenticated ? 'app-workspace' : 'app-workspace app-workspace--public'}>
        {authenticated ? (
          <aside className="desktop-rail" aria-label="Primary">
            <a className="desktop-rail__brand" href={withBasePath('/home')} onClick={(event) => handleLink(event, '/home')}>
              <ArtistNameplate context="Fan Field Kit" />
            </a>
            <nav>
              {desktopItems.map((item, index) => {
                const href = item.label === 'Community' ? communityPath : item.href
                return (
                  <a
                    aria-current={item.ids.includes(activeRoute) ? 'page' : undefined}
                    href={withBasePath(href)}
                    key={item.label}
                    onClick={(event) => handleLink(event, href)}
                  >
                    <span>{String(index + 1).padStart(2, '0')}</span>{item.label}
                  </a>
                )
              })}
            </nav>
            <div className="desktop-rail__utilities">
              <a
                aria-current={activeRoute === 'support' ? 'page' : undefined}
                className="desktop-rail__utility"
                href={withBasePath('/support')}
                onClick={(event) => handleLink(event, '/support')}
              >
                Support
              </a>
              <a
                aria-current={settingsRouteIds.includes(activeRoute) ? 'page' : undefined}
                className="desktop-rail__utility"
                href={withBasePath('/settings')}
                onClick={(event) => handleLink(event, '/settings')}
              >
                Settings
              </a>
            </div>
          </aside>
        ) : null}
        <div className="app-shell">
        <CautionRail />
        {authenticated ? (
          <header className="app-header">
            <div className="app-header__identity">
              <ArtistNameplate compact context={selectedCity || 'On tour'} />
              <strong>{routeLabel(activeRoute)}</strong>
            </div>
            <nav aria-label="Account utilities">
              <TourSoundtrack />
              <a className="app-header__utility app-header__utility--notifications" aria-label={`${notificationCount} unread notifications`} href={withBasePath('/notifications')} onClick={(event) => handleLink(event, '/notifications')}>
                <GlyphIcon name="rock-horns" />
                {notificationCount > 0 ? <span className="app-header__badge">{notificationCount}</span> : null}
              </a>
              <a className="app-header__utility app-header__utility--profile" aria-label="Profile" href={withBasePath('/profile')} onClick={(event) => handleLink(event, '/profile')}><GlyphIcon name="loc-profile" /></a>
            </nav>
          </header>
        ) : (
          <>
            <header className="public-header">
              <ArtistNameplate context="Fan Field Kit" />
              <TourSoundtrack />
            </header>
            <nav aria-label="Explore The Paradox" className="public-nav">
              {publicItems.map((item) => (
                <a
                  aria-current={item.ids.includes(activeRoute) ? 'page' : undefined}
                  href={withBasePath(item.href)}
                  key={item.label}
                  onClick={(event) => handleLink(event, item.href)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </>
        )}
        {!online ? <div className="offline-banner" role="status">Offline — your saved tour data is still available.</div> : null}
        <main className="app-main" id="main-content" tabIndex={-1}>
          {children}
        </main>
        {authenticated ? (
          <>
            <CautionRail placement="bottom" />
            <BottomNav activeRoute={activeRoute} communityPath={communityPath} onNavigate={onNavigate} />
          </>
        ) : null}
        </div>
      </div>
    </div>
  )
}
